import axios from 'axios';

// baseURL empty in Docker (nginx proxies /api to the backend); REACT_APP_API_URL
// overrides it for other setups (19.2's client paragraph).
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
});

// Attach the bearer token from localStorage to every request (contract: token
// lives under 'token', read fresh each call rather than cached on the instance
// so a login/logout in another tab or the same session is picked up immediately).
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// On a 401, clear the stale token and bounce to /login - except for the login
// call itself. Without that exemption a wrong password would redirect the user
// away from /login instead of leaving AuthContext's login() to return the error
// for the page to render as a banner (19.2).
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error?.config?.url ? String(error.config.url) : '';
    const isLoginRequest = url.includes('/auth/login');
    if (error.response && error.response.status === 401 && !isLoginRequest) {
      localStorage.removeItem('token');
      if (window.location.pathname !== '/login') {
        // Keep the deep link. ProtectedRoute adds ?redirect= when it turns an unauthenticated
        // user away, but a token that goes stale mid-session lands here instead, and a bare
        // '/login' silently discarded the page the user was actually on.
        const attempted = window.location.pathname + window.location.search;
        window.location.href = `/login?redirect=${encodeURIComponent(attempted)}`;
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/api/v1/auth/login', credentials),
  // 2026-09-26: 201 body is now { status: "pending", message } -- no token, the
  // account waits for an admin's approval (AuthContext.register no longer stores one).
  register: (userData) => api.post('/api/v1/auth/register', userData),
  getProfile: () => api.get('/api/v1/auth/profile'),
  updateProfile: (payload) => api.patch('/api/v1/auth/profile', payload),
  logout: (config) => api.post('/api/v1/auth/logout', null, config),
  // Anshul, 2026-09-26 review item b: any signed-in user's self-service password
  // change. 200 body is a fresh LoginResponse (access_token + user) -- the caller
  // must store the new token, since the old one is revoked (token_version bumped).
  changePassword: (payload) => api.post('/api/v1/auth/change-password', payload),
};

// Trailing slash on every collection route (contract, P3): FastAPI 307-redirects
// /api/v1/projects -> /api/v1/projects/, so calling the bare path would cost every
// list/create request a second round trip.
export const projectsAPI = {
  list: (params) => api.get('/api/v1/projects/', { params }),
  get: (id) => api.get(`/api/v1/projects/${id}`),
  create: (payload) => api.post('/api/v1/projects/', payload),
  update: (id, payload) => api.put(`/api/v1/projects/${id}`, payload),
  remove: (id) => api.delete(`/api/v1/projects/${id}`),
  videos: (id) => api.get(`/api/v1/projects/${id}/videos`),
};

export const dashboardAPI = {
  metrics: () => api.get('/api/v1/dashboard/metrics'),
};

export default api;
